import tinycolor from 'tinycolor2';
import { Palette } from '@mui/material';

import i18n from 'src/locales';
import { AiQaConfig, MetricScores } from 'src/services/insights/types';

// utils.ts is imported by surfaces outside agent-insights, so these helpers
// bind their own namespaced `t` from the shared i18n instance rather than
// taking a caller-bound `t` param (keeps signatures stable).
const t = i18n.getFixedT(null, 'agent-insights');

export const formatSecondsToReadableHrMinTime = (
  seconds?: number,
  showSeconds = false,
): string => {
  if (!seconds || seconds <= 0) {
    return showSeconds ? t('time.zeroSec') : t('time.zeroMin');
  }

  if (seconds < 60) {
    return t('time.sec', { count: Number(seconds.toFixed(0)) });
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  let result = '';
  if (hours > 0) {
    result += t('time.hr', { count: hours }) + ' ';
  }
  if (minutes > 0) {
    result += t('time.min', { count: minutes }) + ' ';
  }
  if (showSeconds && secs > 0) {
    result += t('time.sec', { count: secs });
  }
  return result.trim();
};

export const getTestAiScoreColor = (score: number): string => {
  if (score < 30) {
    return 'red';
  }
  if (score > 50) {
    return 'green';
  }
  return 'black';
};

export const getPercentChangeAiScoreColor = (
  palette: Palette,
  score: number,
  invert = false,
): string => {
  if (score < 0) {
    return invert ? palette.colors.green[500] : palette.colors.red[500];
  }
  if (score > 0) {
    return invert ? palette.colors.red[500] : palette.colors.green[500];
  }
  return palette.colors.grey[400];
};

export const getAiScoreMetricColor = ({
  isBgColor = false,
  palette,
  score,
}: {
  isBgColor?: boolean;
  palette: Palette;
  score: number | null;
}): string => {
  if (score === -1) {
    if (isBgColor) {
      return palette.colors.grey[100];
    }
    return palette.colors.grey[600];
  }
  if (score === null) {
    return palette.colors.grey[600];
  }

  if (score < 30) {
    return palette.colors.red[500];
  }
  if (score > 50) {
    return palette.colors.green[500];
  }

  if (isBgColor) {
    return palette.colors.yellow[500];
  }
  return palette.colors.black;
};

export const getRandomColor = () => {
  const letters = '0123456789ABCDEF';
  let color = '';
  do {
    color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
  } while (tinycolor.readability(color, 'white') < 4.5);
  return color;
};

export const getEligibleColorFromBgColor = (color: string) => {
  const isEligible = tinycolor.readability(color, 'white') >= 4.5;
  if (isEligible) {
    return 'white';
  }
  return 'black';
};

export const replaceAgentEmailInRoute = (path: string, agentId: string) =>
  path.replace(':agentEmail', agentId);

export const generateScoresBasedOnAverage = (
  length: number,
  targetAverage: number,
): number[] => {
  if (length === 0) {
    return [];
  }
  if (length === 1) {
    return [targetAverage];
  }

  const values: number[] = [];
  let sum = 0;

  // Generate random values for all but the last element
  for (let i = 0; i < length - 1; i++) {
    // Generate values between 0 and 100 with some variation around the target
    const minValue = Math.max(0, targetAverage - 30);
    const maxValue = Math.min(100, targetAverage + 30);
    const randomValue =
      Math.floor(Math.random() * (maxValue - minValue + 1)) + minValue;
    values.push(randomValue);
    sum += randomValue;
  }

  // Calculate the last value to achieve the exact average
  const lastValue = Math.max(0, Math.min(100, targetAverage * length - sum));
  values.push(lastValue);

  return values;
};

export const calculatePercentChange = (
  current?: number | null,
  previous?: number | null,
): number => {
  // Return 0 if either value is null/undefined or if previous is 0 (avoid division by zero)
  if (!current || !previous || previous === 0) {
    return 0;
  }
  return (current - previous) / previous;
};

export const formatSummaryOrReasoning = (line: string) => {
  // Replace hyphens with bullet points at start of lines (including indented)
  // Pattern: ^ (start of line) + (\s*) (capture whitespace) + - + \s (space)
  // Replacement: preserved whitespace + bullet + space
  return line.replace(/^(\s*)-\s/gm, '$1• ');
};

/**
 * Compute the average QA score across rubrics, optionally using custom weights.
 * When weighted scoring is enabled and weights are provided, computes a weighted
 * average. Otherwise falls back to a simple arithmetic mean.
 */
/**
 * Initialize rubric weights for first-time weighted scoring setup.
 * Excluded rubrics (exclude_score === true) get weight 0.
 * Remaining rubrics share 100 equally via floor + remainder distribution.
 */
export const initializeRubricWeights = (
  activeRubrics: AiQaConfig[],
): Record<string, number> => {
  if (activeRubrics.length === 0) {
    return {};
  }

  const included = activeRubrics.filter(r => r.exclude_score !== true);
  const excluded = activeRubrics.filter(r => r.exclude_score === true);

  const count = included.length;
  const base = count > 0 ? Math.floor(100 / count) : 0;
  const remainder = count > 0 ? 100 - base * count : 0;

  const result: Record<string, number> = {};
  included.forEach((r, i) => {
    result[r.model_id] = base + (i < remainder ? 1 : 0);
  });
  excluded.forEach(r => {
    result[r.model_id] = 0;
  });

  return result;
};

export function shallowRecordEqual(
  a: Record<string, number> | null,
  b: Record<string, number>,
): boolean {
  if (a === null) {
    return false;
  }
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) {
    return false;
  }
  return keysA.every(key => a[key] === b[key]);
}

export const computeWeightedAvgQaScore = (
  scores: Record<string, MetricScores>,
  rubricWeights: Record<string, number> | undefined,
  weightedScoringEnabled: boolean,
): number => {
  if (
    weightedScoringEnabled &&
    rubricWeights &&
    Object.keys(rubricWeights).length > 0
  ) {
    let weightedSum = 0;
    let totalWeight = 0;
    for (const [key, score] of Object.entries(scores)) {
      const w = rubricWeights[key] ?? 0;
      if (w > 0) {
        weightedSum += w * score.avg_score;
        totalWeight += w;
      }
    }
    return totalWeight > 0 ? weightedSum / totalWeight : 0;
  } else {
    const eligible = Object.entries(scores);

    if (eligible.length === 0) {
      return 0;
    }

    return (
      eligible.reduce((acc, [, s]) => acc + s.avg_score, 0) / eligible.length
    );
  }
};
